import { sortBy } from 'lodash';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MultiColumnComboBoxComponent, PreventableEvent } from '@progress/kendo-angular-dropdowns';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { MemberListService } from 'src/app/core/service/grid-data/grid-data-member.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { TaskListService } from 'src/app/core/service/grid-data/grid-data-task.service';
import { DocumentService } from 'src/app/core/service/document.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { DocumentViewerModel } from 'src/app/shared/document-viewer/model/document-viewer';
import { DocumentViewerComponent } from 'src/app/shared/document-viewer/document-viewer.component';
import { TeamService } from 'src/app/core/service/team.service';
import { NotesService } from 'src/app/core/service/notes.service';
import { FlagService } from 'src/app/core/service/flag.service';
import { CaseListService } from 'src/app/core/service/grid-data/grid-data-case.service';
import { InsuranceListService } from 'src/app/core/service/grid-data/grid-data-insurance.service';
import { PhonePipe } from 'src/app/shared/pipes/phone.pipe';
import { UserService } from 'src/app/core/service/user.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { LanguageService } from 'src/app/core/service/language.service';
import { HelperUtils } from 'src/app/core/utility/helper-utils';
import { MemberService } from 'src/app/core/service/member.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { GoalService } from 'src/app/core/service/goal.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-patient-merge-selector',
  templateUrl: './patient-merge-selector.component.html',
  // styleUrls: ['./patient-merge-selector.component.scss']
  encapsulation: ViewEncapsulation.None,
  styles: [`
  .k-step-list-horizontal .k-step-link {
    max-width: 100%;
  }
  .k-panelbar .k-icon { font-family: 'WebComponentsIcons'; }
  .k-i-arrow-n::before { content: "\\e013"; }
  .k-i-arrow-s::before { content: "\\e015"; }
  .badge-merge {
    width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 10px;
    text-align:center;
    background-color: #FB8C00;
    margin-left: 10px;
  }
  .max-grid-height { max-height: 500px; }
  .subject-popup { font-size: 11pt; }
  `]
})
export class PatientMergeSelectorComponent implements OnInit, AfterViewInit
{
  @ViewChild('combobox') public subjectSelector: MultiColumnComboBoxComponent;
  @ViewChild('documentViewer') public documentViewer: DocumentViewerComponent;

  @Input() public disabledToggleOnView = false;
  @Input() public defaultSubject = null;
  @Input() public defaultOperator = null;
  @Input() public context = null;
  @Input() public excludeId = null;       // To exclude on the target the selected member from the target
  @Input() public searchEnabled = false;
  @Input() public propertiesSelectorVisible = false;
  @Input() public propertiesSelectorEnabled = false;

  @Output() public subjectChange: EventEmitter<any> = new EventEmitter();
  @Output() public operatorChange: EventEmitter<any> = new EventEmitter();

  public selectedSubject = null;
  public subjectItems: Array<any> = [];
  public patientLabel = {};
  public operator = 'startsWith';
  public insurancesEnabled = false;
  public showDependents = false;
  public goalsEnabled = false;
  public attachmentsEnabled = false;
  public sourceMember = null;
  public allTribalAffiliations = [];
  public allTribalEnrollments = [];
  public showGender = false;
  public showAdministrativeGender = false;

  public viewDemographics: GridViewManager;
  public viewCases: GridViewManager;
  public viewTasks: GridViewManager;
  public viewInsurances: GridViewManager;
  public viewDocuments: GridViewManager;
  public viewFlags: GridViewManager;
  public viewCareTeams: GridViewManager;
  public viewNotes: GridViewManager;
  public viewGoals: GridViewManager;
  public viewExtensions: GridViewManager;
  public viewHouseholdMembers: GridViewManager;

  public caseCount = 0;
  public taskCount = 0;
  public insuranceCount = 0;
  public demographicCount = 0;
  public documentCount = 0;
  public flagCount = 0;
  public careTeamCount = 0;
  public noteCount = 0;
  public totalChildrenCodeSet = [];
  public totalAdultsCodeSet = [];
  public goalCount = 0;
  public extensionCount = 0;
  public householdMembersCount = 0;
  public relationships = [];
  public membersInMyHousehold = '';
  public partOfAHouseholdCannotMerge = '';

  private minOpenSize = 3;                // Minimum number of characters before querying the subject
  private maxLoadedItems = 99999;

  constructor(
    private memberListService: MemberListService,
    private caseListService: CaseListService,
    private taskListService: TaskListService,
    private insuranceListService: InsuranceListService,
    private documentService: DocumentService,
    private teamService: TeamService,
    private flagService: FlagService,
    private noteService: NotesService,
    private translate: TranslateService,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private languageService: LanguageService,
    public intl: IntlService,
    public memberService: MemberService,
    private dictionaryService: DictionaryService,
    private goalService: GoalService
  ) { }

  /**
   * Provide loading property indeicators for list.
   */
  public get loadingSubjects(): boolean
  {
    return this.memberListService.loading;
  }

  public get loadingCases(): boolean
  {
    return this.caseListService.loading;
  }

  public get loadingFlags(): boolean
  {
    return this.caseListService.loading;
  }

  public get loadingInsurances(): boolean
  {
    return this.insuranceListService.loading;
  }

  public ngOnInit(): void
  {
    this.operator = this.defaultOperator !== null ? this.defaultOperator : 'startsWith';
    this.viewDemographics = new GridViewManager(this.maxLoadedItems);
    this.viewCases = new GridViewManager(this.maxLoadedItems);
    this.viewTasks = new GridViewManager(this.maxLoadedItems);
    this.viewInsurances = new GridViewManager(this.maxLoadedItems);
    this.viewDocuments = new GridViewManager(this.maxLoadedItems);
    this.viewFlags = new GridViewManager(this.maxLoadedItems);
    this.viewNotes = new GridViewManager(this.maxLoadedItems);
    this.viewCareTeams = new GridViewManager(this.maxLoadedItems);
    this.viewGoals = new GridViewManager(this.maxLoadedItems);
    this.viewExtensions = new GridViewManager(this.maxLoadedItems);
    this.viewHouseholdMembers = new GridViewManager(this.maxLoadedItems);
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.showDependents = sessionSetting.showDependents;
        this.insurancesEnabled = (sessionSetting.insurancesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.insurancesRoles) : false;
        this.goalsEnabled = (sessionSetting.goalsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.goalsRoles) : false;
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.attachmentsRoles) : false;
        this.showGender = sessionSetting.showGender;
        this.showAdministrativeGender = sessionSetting.showAdministrativeGender;
      }
    });

    forkJoin([
      this.dictionaryService.getCachedTotalChildren(),
      this.dictionaryService.getCachedTotalAdults(),
      this.dictionaryService.getCachedTribalEnrollments(),
      this.dictionaryService.getCachedTribalAffiliations()]
    ).subscribe(responseList =>
    {
      this.totalChildrenCodeSet = responseList[0];
      this.totalAdultsCodeSet = responseList[1];
      this.allTribalEnrollments = responseList[2];
      this.allTribalAffiliations = responseList[3];
    });

    this.dictionaryService.getCachedFamilyMembers().subscribe({
      next: (data) =>
      {
        this.relationships = data;
      }
    });   

    this.translate.get(['HOUSEHOLD-MEMBERS']).subscribe({
      next: (res) =>
      {
        this.membersInMyHousehold = res['HOUSEHOLD-MEMBERS'];       
      }
    });     
  }

  public ngAfterViewInit(): void
  {
    // Default subject is set on the target by default based on the source subject.
    if (this.defaultSubject !== null)
    {
      this.handleSubjectFilterChange(this.defaultSubject.fullName);
      if (!this.disabledToggleOnView)
      {
        this.subjectSelector.toggle();
      }
    }
  }

  /**
   * Event occurs when a subject selection have been changed.
   *
   * @param subject Selected new subject.
   */
  public onChangeSubject(subject: any): void
  {
    // Set the new selected subject and propage selection change.
    this.selectedSubject = subject !== undefined ? subject : null;

    if (this.selectedSubject !== null && this.selectedSubject.id > 0)
    {
      this.memberService.isMemberPartOfAHousehold(this.selectedSubject.id).subscribe({
        next: (response) =>
        {
          if (response !== null)
          {
            this.translate.get(['MEMBER-PART-OF-HOUSEHOLD-CANNOT-MERGE']).subscribe({
              next: (res) =>
              {
                this.partOfAHouseholdCannotMerge = res['MEMBER-PART-OF-HOUSEHOLD-CANNOT-MERGE'];
                this.messageHandlerService.displayDirectMessage('error', this.partOfAHouseholdCannotMerge.replace('{{headOfHousehold}}', response.fullName));
                return;
              }
            }); 
          }
          else
          { 
            this.subjectChange.emit(this.selectedSubject);

            // Load subject data
            this.loadDemographics(this.selectedSubject);
            this.loadHouseholdMembers(this.selectedSubject);
            this.loadDocuments(this.selectedSubject);
            this.loadCareTeams(this.selectedSubject);
            this.loadCases(this.selectedSubject);
            this.loadFlags(this.selectedSubject);
            this.loadInsurances(this.selectedSubject);
            this.loadNotes(this.selectedSubject);
            this.loadTasks(this.selectedSubject);
            this.loadGoals(this.selectedSubject);
            this.loadExtensions(this.selectedSubject);
          }
        }
      });
    }
  }

  /**
   * Method call from parent when cancelling a merge. Selection is cleared.
   */
  public clearSelection(): void
  {
    this.onChangeSubject(null);
  }

  /**
   * Event called when changing the operator.
   *
   * @param operator Filter operator is changed.
   */
  public changeFilterOperator(operator: 'startsWith' | 'contains'): void
  {
    this.operator = operator;
    this.operatorChange.emit(operator);
  }

  /**
   * Subject filter changed.
   *
   * @param searchTerm New search value entered.
   */
  public handleSubjectFilterChange(searchTerm: string, newSubject = null): void
  {
    if (searchTerm.length >= this.minOpenSize)
    {
      const subjectState: State = { skip: 0, take: 50, filter: { logic: 'and', filters: [] } };
      subjectState.filter.filters.push({ field: 'fullName', operator: this.operator.toLocaleLowerCase(), value: searchTerm });

      // On surviving subject, exclude selected source subject id
      if (this.excludeId !== null && this.excludeId !== undefined)
      {
        subjectState.filter.filters.push({ field: 'memberId', operator: 'neq', value: this.excludeId });
      }

      this.memberListService.queryByState(subjectState, (value: any) =>
      {
        this.subjectItems = value.data;
        if (newSubject !== null)
        {
          this.onChangeSubject(newSubject);
        }
      });
    }
  }

  /**
   * Data change event handler for the different views.
   *
   * @param view  View to change state for
   * @param state State of grid operations
   */
  public dataStateChange(view: GridViewManager, state: DataStateChangeEvent): void
  {
    view.setState(state);
  }

  /**
   * Subject selection will dropdown only if there are three or more character selected.
   *
   * @param event Prevent drop-down opening if minimum number of characters is not reach.
   */
  public handleOpenSubject(event: PreventableEvent): void
  {
    if (this.subjectSelector.text.length < this.minOpenSize)
    {
      event.preventDefault();
    }
  }

  /**
   * Return the proper class to used based on the file mime type.
   *
   * @param mimeType Mime Type of the file.
   * @returns String
   */
  public getFileTypeClass(mimeType: string): string
  {
    return this.documentService.getFileTypeClass(mimeType);
  }

  /**
   * View document handler
   *
   * @param url Document Url
   * @param description Document description
   * @param mediaType Media mime type
   * @param createdOn Document created date
   */
  public viewDocumentHandler(url: string, description: string, mediaType: string, createdOn: string): void
  {
    const model = new DocumentViewerModel();
    model.url = url;
    model.description = description;
    model.mediaType = mediaType;
    model.createdOn = createdOn;

    this.documentViewer.execute(model);
  }


  /**
   * Load Subject demographics for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadDemographics(subject: any): void
  {
    let total = 0;
    const demographics = [];

    if (subject !== null)
    {
      // Notification value prepration
      let notificationValue = '';
      switch (subject.notificationType)
      {
        case 'EMAIL':
          notificationValue = this.translate.instant('EMAIL');
          break;
        case 'SMS':
          notificationValue = this.translate.instant('SMS');
          break;
        case 'NO_NOTIFICATION':
          notificationValue = this.translate.instant('NO_NOTIFICATION');
          break;
      }

      let livesAloneValue = '';
      if (subject.livesAlone !== '')
      {
        livesAloneValue = subject.livesAlone ? this.translate.instant('YES') : this.translate.instant('NO');
      }

      let primaryCaregiverValue = '';
      if (subject.primaryCaregiver !== '')
      {
        primaryCaregiverValue = subject.primaryCaregiver ? this.translate.instant('YES') : this.translate.instant('NO');
      }

      let tribalAffiliatedValue = '';
      if (subject.tribalAffiliated !== '')
      {
        tribalAffiliatedValue = subject.tribalAffiliated ? this.translate.instant('YES') : this.translate.instant('NO');
      }

      let tribalEnrolledValue = '';
      if (subject.tribalEnrolled !== '')
      {
        tribalEnrolledValue = subject.tribalEnrolled ? this.translate.instant('YES') : this.translate.instant('NO');
      }

      demographics.push({
        name: this.translate.instant('MEMBER-ID', this.patientLabel), value: subject.id,
        key: 'id'
      });
      demographics.push({
        name: this.translate.instant('FIRST-NAME'), value: subject.givenName ? subject.givenName : '',
        key: 'givenName'
      });
      demographics.push({
        name: this.translate.instant('LAST-NAME'), value: subject.familyName ? subject.familyName : '',
        key: 'familyName'
      });
      demographics.push({
        name: this.translate.instant('MIDDLE-NAME'), value: subject.middleName ? subject.middleName : '',
        key: 'middleName'
      });
      demographics.push({
        name: this.translate.instant('PREFERRED-NAME'), value: subject.preferredName ? subject.preferredName : '',
        key: 'preferredName'
      });
      if (this.showGender)
      {
        demographics.push({
          name: this.translate.instant('GENDER'), value: subject.gender ? subject.gender : '',
          key: 'gender'
        });
      }
      if (this.showAdministrativeGender)
      {
        demographics.push({
          name: this.translate.instant('ADMINISTRATIVE-GENDER'), value: subject.administrativeGender ? subject.administrativeGender : '',
          key: 'administrativeGender'
        });
      }
      demographics.push({
        name: this.translate.instant('DATE-OF-BIRTH'), value: subject.dateOfBirth ?
          this.intl.formatDate(this.intl.parseDate(subject.dateOfBirth), 'd') : '',
        key: 'dateOfBirth'
      });
      demographics.push({
        name: this.translate.instant('EMAIL'), value: subject.email ? subject.email : '',
        key: 'email'
      });
      demographics.push({
        name: this.translate.instant('NOTIFICATION'), value: notificationValue,
        key: 'notificationType'
      });
      demographics.push({
        name: this.translate.instant('HOME-PHONE'),
        value: subject.homePhoneNumber ? new PhonePipe(this.languageService).transform(subject.homePhoneNumber) : '',
        key: 'homePhoneNumber'
      });
      demographics.push({
        name: this.translate.instant('HOME-PHONE-SAFE'),
        value: subject.homePhoneSafe ? this.translate.instant('YES') : this.translate.instant('NO'),
        key: 'homePhoneSafe'
      });
      demographics.push({
        name: this.translate.instant('CELL-PHONE'),
        value: subject.cellPhoneNumber ? new PhonePipe(this.languageService).transform(subject.cellPhoneNumber) : '',
        key: 'cellPhoneNumber'
      });
      demographics.push({
        name: this.translate.instant('CELL-PHONE-SAFE'),
        value: subject.cellPhoneSafe ? this.translate.instant('YES') : this.translate.instant('NO'),
        key: 'cellPhoneSafe'
      });
      demographics.push({
        name: this.translate.instant('WORK-PHONE-LABEL'),
        value: subject.workPhoneNumber ? new PhonePipe(this.languageService).transform(subject.workPhoneNumber) : '',
        key: 'workPhoneNumber'
      });
      demographics.push({
        name: this.translate.instant('WORK-PHONE-SAFE'),
        value: subject.workPhoneSafe ? this.translate.instant('YES') : this.translate.instant('NO'),
        key: 'workPhoneSafe'
      });
      demographics.push({
        name: this.translate.instant('WORK-PHONE-LABEL') + ' ' + this.translate.instant('PHONE-EXTENSION-PREFIX'),
        value: subject.workPhoneNumberExtension ? subject.workPhoneNumberExtension : '',
        key: 'workPhoneNumberExtension'
      });
      demographics.push({
        name: this.translate.instant('OTHER-PHONE-LABEL'),
        value: subject.otherPhoneNumber ? new PhonePipe(this.languageService).transform(subject.otherPhoneNumber) : '',
        key: 'otherPhoneNumber'
      });
      demographics.push({
        name: this.translate.instant('OTHER-PHONE-SAFE'),
        value: subject.otherPhoneSafe ? this.translate.instant('YES') : this.translate.instant('NO'),
        key: 'otherPhoneSafe'
      });
      demographics.push({
        name: this.translate.instant('PAGER'),
        value: subject.pagerNumber ? new PhonePipe(this.languageService).transform(subject.pagerNumber) : '',
        key: 'pagerNumber'
      });
      demographics.push({
        name: this.translate.instant('PAGER-SAFE'),
        value: subject.pagerSafe ? this.translate.instant('YES') : this.translate.instant('NO'),
        key: 'pagerSafe'
      });
      demographics.push({
        name: this.translate.instant('FAX-OR-OTHER'),
        value: subject.faxNumber ? new PhonePipe(this.languageService).transform(subject.faxNumber) : '',
        key: 'faxNumber'
      });
      demographics.push({
        name: this.translate.instant('FAX-SAFE'),
        value: subject.faxSafe ? this.translate.instant('YES') : this.translate.instant('NO'),
        key: 'faxSafe'
      });
      demographics.push({
        name: this.translate.instant('URL'),
        value: subject.url ? new PhonePipe(this.languageService).transform(subject.url) : '',
        key: 'url'
      });
      demographics.push({
        name: this.translate.instant('PREFERRED-VIDEO-PROVIDER'),
        value: subject.preferredVideoProvider ? subject.preferredVideoProvider : '',
        key: 'preferredVideoProvider'
      });
      demographics.push({
        name: this.translate.instant('NOTIFICATION-LANGUAGE'),
        value: subject.preferredLanguage ? subject.preferredLanguage : '',
        key: 'preferredLanguage'
      });
      demographics.push({
        name: this.translate.instant('STREET'), value: subject.streetLine ? subject.streetLine : '',
        key: 'streetLine'
      });
      demographics.push({
        name: this.translate.instant('ADDRESS-LINE-2'),
        value: subject.streetLineAdditional ? subject.streetLineAdditional : '',
        key: 'streetLineAdditional'
      });
      demographics.push({
        name: this.translate.instant('CITY'), value: subject.city ? subject.city : '',
        key: 'city'
      });
      demographics.push({
        name: this.translate.instant('STATE'), value: subject.stateDisplayName ? subject.stateDisplayName : '',
        key: 'state'
      });
      demographics.push({
        name: this.translate.instant('COUNTY'), value: subject.county ? subject.county : '',
        key: 'county'
      });
      demographics.push({
        name: this.translate.instant('POSTAL-CODE'), value: subject.postalCode ? subject.postalCode : '',
        key: 'postalCode'
      });
      demographics.push({
        name: this.translate.instant('COUNTRY'), value: subject.countryDisplayName ? subject.countryDisplayName : '',
        key: 'country'
      });

      if (subject.addresses !== null && subject.addresses.length > 0)
      {
        subject.addresses.forEach((address) =>
        {
          if (!HelperUtils.isPrimaryAddress(address,
            subject.streetLine, subject.streetLineAdditional, subject.city, subject.county, subject.postalCode,
            subject.stateCode, subject.countryCode, subject.addressTypeCode))
          {
            demographics.push({
              name: this.translate.instant('ADDRESS') + ' (' + address.addressType.displayName + ')',
              value: HelperUtils.formatFullAddress(address),
              key: 'addresses'
            });
          }
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('ADDRESS'), value: '', key: 'addresses' });
      }
      if (subject.raceEthnicities !== null && subject.raceEthnicities.length > 0)
      {
        let allRaceEthnicities = '';
        subject.raceEthnicities.forEach((raceEthnicity) =>
        {
          allRaceEthnicities = allRaceEthnicities + `${raceEthnicity.displayName}, `;
        });
        demographics.push({
          name: this.translate.instant('RACE-ETHNICITIES'),
          value: allRaceEthnicities.substring(0, allRaceEthnicities.length - 2),
          key: 'raceEthnicities'
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('RACE-ETHNICITIES'), value: '', key: 'raceEthnicities' });
      }
      demographics.push({
        name: this.translate.instant('MARITAL-STATUS'), value: subject.maritalStatus ? subject.maritalStatus : '',
        key: 'maritalStatus'
      });
      demographics.push({
        name: this.translate.instant('RELIGIOUS-AFFILIATION'),
        value: subject.religiousAffiliation ? subject.religiousAffiliation : '',
        key: 'religiousAffiliation'
      });
      if (subject.spokenLanguages !== null && subject.spokenLanguages.length > 0)
      {
        let allSpokenLanguages = '';
        subject.spokenLanguages.forEach((spokenLanguage) =>
        {
          allSpokenLanguages = allSpokenLanguages + `${spokenLanguage.displayName}, `;
        });
        demographics.push({
          name: this.translate.instant('SPOKEN-LANGUAGES'),
          value: allSpokenLanguages.substring(0, allSpokenLanguages.length - 2),
          key: 'spokenLanguages'
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('SPOKEN-LANGUAGES'), value: '', key: 'spokenLanguages' });
      }
      if (subject.memberWrittenLanguages !== null && subject.memberWrittenLanguages.length > 0)
      {
        let allWrittenLanguages = '';
        subject.memberWrittenLanguages.forEach((written) =>
        {
          allWrittenLanguages = allWrittenLanguages + `${written.writtenLanguage.displayName}, `;
        });
        demographics.push({
          name: this.translate.instant('WRITTEN-LANGUAGES'),
          value: allWrittenLanguages.substring(0, allWrittenLanguages.length - 2),
          key: 'writtenLanguages'
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('WRITTEN-LANGUAGES'), value: '', key: 'writtenLanguages' });
      }
      if (subject.memberReadingLanguages !== null && subject.memberReadingLanguages.length > 0)
      {
        let allReadingLanguages = '';
        subject.memberReadingLanguages.forEach((reading) =>
        {
          allReadingLanguages = allReadingLanguages + `${reading.readingLanguage.displayName}, `;
        });
        demographics.push({
          name: this.translate.instant('READING-LANGUAGES'),
          value: allReadingLanguages.substring(0, allReadingLanguages.length - 2),
          key: 'readingLanguages'
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('READING-LANGUAGES'), value: '', key: 'readingLanguages' });
      }
      demographics.push({
        name: this.translate.instant('EDUCATION-LEVEL'), value: subject.educationLevel ? subject.educationLevel : '',
        key: 'educationLevel'
      });
      demographics.push({
        name: this.translate.instant('EMPLOYMENT-STATUS'),
        value: subject.employmentStatus ? subject.employmentStatus : '',
        key: 'employmentStatus'
      });
      demographics.push({
        name: this.translate.instant('VETERAN-STATUS'), value: subject.veteranStatus ? subject.veteranStatus : '',
        key: 'veteranStatus'
      });
      demographics.push({
        name: this.translate.instant('FAMILY-INCOME'), value: subject.familyIncome ? subject.familyIncome : '',
        key: 'familyIncome'
      });
      demographics.push({
        name: this.translate.instant('POVERTY-LEVEL'), value: subject.povertyLevel ? subject.povertyLevel : '',
        key: 'povertyLevel'
      });
      demographics.push({
        name: this.translate.instant('HOUSING-STATUS'), value: subject.housingStatus ? subject.housingStatus : '',
        key: 'housingStatus'
      });
      demographics.push({
        name: this.translate.instant('HOUSEHOLD-SIZE'), value: subject.householdSize ? subject.householdSize : '',
        key: 'householdSize'
      });
      demographics.push({
        name: this.translate.instant('PREFERRED-CONTACT-METHODS'), value: subject.preferredContactMethods ? subject.preferredContactMethods : '',
        key: 'preferredContactMethods'
      });

      if (this.showDependents)
      {
        demographics.push({
          name: this.translate.instant('DEPENDENTS'), value: subject.dependents ? subject.dependents : '',
          key: 'dependents'
        });
      }

      if (this.totalChildrenCodeSet.length > 0)
      {
        if (subject.memberChildren !== null && subject.memberChildren.length > 0)
        {
          subject.memberChildren = sortBy(subject.memberChildren, ['childrenId']);
          subject.memberChildren.forEach((child) =>
          {
            const ageBracket: Dictionary[] = this.totalChildrenCodeSet.filter(e => e.id === child.childrenId);
            const displayName = ageBracket !== undefined && ageBracket.length === 1 ? ageBracket[0].displayName : '';
            demographics.push({
              name: this.translate.instant('TOTAL-CHILDREN') + `: ${displayName}`,
              value: child.value,
              key: 'memberChildren-' + `${child.childrenId}`
            });
          });
        }
        else
        {
          // Just to make sure source and target shows the same number of demographics lines
          demographics.push({ name: this.translate.instant('TOTAL-CHILDREN'), value: '', key: 'memberChildren' });
        }
      }

      if (this.totalAdultsCodeSet.length > 0) 
      {
        if (subject.memberAdults !== null && subject.memberAdults.length > 0)
        {
          subject.memberAdults = sortBy(subject.memberAdults, ['adultId']);
          subject.memberAdults.forEach((adult) =>
          {
            const ageBracket: Dictionary[] = this.totalAdultsCodeSet.filter(e => e.id === adult.adultId);
            const displayName = ageBracket !== undefined && ageBracket.length === 1 ? ageBracket[0].displayName : '';
            demographics.push({
              name: this.translate.instant('TOTAL-ADULTS') + `: ${displayName}`,
              value: adult.value,
              key: 'memberAdult-' + `${adult.adultId}`
            });
          });
        }
        else
        {
          // Just to make sure source and target shows the same number of demographics lines
          demographics.push({ name: this.translate.instant('TOTAL-ADULTS'), value: '', key: 'memberAdults' });
        }
      }

      demographics.push({
        name: this.translate.instant('LIVES-ALONE'), value: livesAloneValue,
        key: 'livesAlone'
      });

      demographics.push({
        name: this.translate.instant('PRIMARY-CAREGIVER'), value: primaryCaregiverValue,
        key: 'primaryCaregiver'
      });

      if (subject.races !== null && subject.races.length > 0)
      {
        let allRaces = '';
        subject.races.forEach((race) =>
        {
          allRaces = allRaces + `${race.displayName}, `;
        });
        demographics.push({
          name: this.translate.instant('RACES'),
          value: allRaces.substring(0, allRaces.length - 2),
          key: 'races'
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('RACES'), value: '', key: 'races' });
      }
      demographics.push({
        name: this.translate.instant('SEXUAL-ORIENTATION'), value: subject.sexualOrientation ? subject.sexualOrientation : '',
        key: 'sexualOrientation'
      });
      demographics.push({
        name: this.translate.instant('NAME-PRONOUN'), value: subject.namePronoun ? subject.namePronoun : '',
        key: 'namePronoun'
      });
      demographics.push({
        name: this.translate.instant('NAME-PRONOUN-OTHER'), value: subject.namePronounOther ? subject.namePronounOther : '',
        key: 'namePronounOther'
      });
      demographics.push({
        name: this.translate.instant('ENGLISH-PROFICIENCY'), value: subject.englishProficiency ? subject.englishProficiency : '',
        key: 'englishProficiency'
      });
      demographics.push({
        name: this.translate.instant('INTERPRETER-NEED'), value: subject.interpreterNeed ? subject.interpreterNeed : '',
        key: 'interpreterNeed'
      });
      demographics.push({
        name: this.translate.instant('INTERPRETER-NEED-OTHER'), value: subject.interpreterNeedOther ? subject.interpreterNeedOther : '',
        key: 'interpreterNeedOther'
      });

      if (this.allTribalAffiliations.length > 0) 
      {
        demographics.push({
          name: this.translate.instant('TRIBAL-AFFILIATED'), value: tribalAffiliatedValue,
          key: 'tribalAffiliated'
        });

        if (subject.tribalAffiliations !== null && subject.tribalAffiliations.length > 0)
        {
          let allTribalAffiliations = '';
          subject.tribalAffiliations.forEach((tribalAffiliation) =>
          {
            allTribalAffiliations = allTribalAffiliations + `${tribalAffiliation.displayName} (${tribalAffiliation.code}), `;
          });
          demographics.push({
            name: this.translate.instant('TRIBAL-AFFILIATIONS'),
            value: allTribalAffiliations.substring(0, allTribalAffiliations.length - 2),
            key: 'tribalAffiliations'
          });
        }
        else
        {
          // Just to make sure source and target shows the same number of demographics lines
          demographics.push({ name: this.translate.instant('TRIBAL-AFFILIATIONS'), value: '', key: 'tribalAffiliations' });
        }
      }

      if (this.allTribalEnrollments.length > 0) 
      {
        demographics.push({
          name: this.translate.instant('TRIBAL-ENROLLED'), value: tribalEnrolledValue,
          key: 'tribalEnrolled'
        });

        if (subject.tribalEnrollments !== null && subject.tribalEnrollments.length > 0)
        {
          let allTribalEnrollments = '';
          subject.tribalEnrollments.forEach((tribalEnrollment) =>
          {
            allTribalEnrollments = allTribalEnrollments + `${tribalEnrollment.displayName} (${tribalEnrollment.code}), `;
          });
          demographics.push({
            name: this.translate.instant('TRIBAL-ENROLLMENTS'),
            value: allTribalEnrollments.substring(0, allTribalEnrollments.length - 2),
            key: 'tribalEnrollments'
          });
        }
        else
        {
          // Just to make sure source and target shows the same number of demographics lines
          demographics.push({ name: this.translate.instant('TRIBAL-ENROLLMENTS'), value: '', key: 'tribalEnrollments' });
        }
      }

      if (subject.mrn !== null)
      {
        demographics.push({
          name: this.translate.instant('CHANNELS-RECORD-NUMBER'), value: subject.mrn,
          key: 'mrn'
        });
      }

      if (subject.identifiers !== null && subject.identifiers.length > 0)
      {
        let identifierNumber = 1;
        subject.identifiers.forEach((identifier) =>
        {
          demographics.push({
            name: this.translate.instant('IDENTIFIER-VALUE') + ` ${identifierNumber}`,
            value: `${identifier.type.code}^${identifier.value}` +
              (identifier.source !== null ? '^' + identifier.source.code : ''),
            key: 'identifiers'
          });
          identifierNumber += 1;
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('IDENTIFIER-VALUE'), value: '', key: 'identifiers' });
      }

      if (subject.enhancedServices !== undefined && subject.enhancedServices !== null && subject.enhancedServices.length > 0)
      {
        let allEnhancedServices = '';
        subject.enhancedServices.forEach((enhancedService) =>
        {
          allEnhancedServices = allEnhancedServices + `${enhancedService.displayName}, `;
        });
        demographics.push({
          name: this.translate.instant('ENHANCED-SERVICE-ELIGIBILITY'),
          value: allEnhancedServices.substring(0, allEnhancedServices.length - 2),
          key: 'enhancedServices'
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('ENHANCED-SERVICE-ELIGIBILITY'), value: '', key: 'enhancedServices' });
      }

      if (subject.otherNames !== null && subject.otherNames.length > 0)
      {
        let nameNumber = 1;        
        subject.otherNames.forEach((name) =>
        {          
          demographics.push({
            name: this.translate.instant('OTHER-NAME') + ` ${nameNumber}`,
            value: (name.givenName !== null ? name.givenName + ' ' : '') + (name.familyName !== null ? name.familyName : ' ') +
              (name.nameType !== null ? ' (' + name.nameType.displayName + ')' : ''),
            key: 'otherNames-' + `${name.nameType.id}`
          });
          nameNumber += 1;
        });
      }
      else
      {
        // Just to make sure source and target shows the same number of demographics lines
        demographics.push({ name: this.translate.instant('OTHER-NAME'), value: '', key: 'otherNames' });
      }      

      // Count none blank demographics
      demographics.forEach((d) =>
      {
        if (d.key === 'preferredContactMethods' && d.value && d.value.length > 0) 
        {
          d.value = this.memberService.convertPreferredContactMethodsToDisplayTexts(d.value.join());
        }
        d.selected = false;
        if (d.value && d.value !== '')
        {
          total += 1;
        }
      });
    }
    this.demographicCount = total;
    this.viewDemographics.setData(demographics);
  }

  /**
   * Load Documents for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadDocuments(subject: any): void
  {
    if (this.insurancesEnabled)
    {
      this.documentCount = 0;
      this.viewDocuments.setData([]);
      if (subject !== null)
      {
        this.viewDocuments.startLoading();
        this.documentService.searchDocumentsByMember(subject.id).subscribe({
          next: (loadedDocuments) =>
          {
            this.documentCount = loadedDocuments.total;
            const data = [];
            loadedDocuments.data.forEach(document =>
            {
              data.push({
                url: document.url,
                description: document.name,
                mediaType: document.mimeType,
                date: new Date(document.createdOn),
                createdBy: document.createdBy,
                isDownloadDisabled: document.isDownloadDisabled
              });
            });
            this.viewDocuments.setData(data);
            this.viewDocuments.setSort([{ field: 'date', dir: 'desc' }]);
          },
          error: (error) =>
          {
            this.messageHandlerService.displayError(error);
          },
          complete: () =>
          {
            this.viewDocuments.stopLoading();
          }
        });
      }
    }
  }

  /**
    * Load Household Members for the given subject.
    *
    * @param subject Current subject selected.
    */
  private loadHouseholdMembers(subject: any): void
  {
    this.householdMembersCount = 0;
    this.viewHouseholdMembers.setData([]);
    if (subject !== null)
    {
      this.viewHouseholdMembers.startLoading();
      this.memberService.getLinkedMember(subject.id).subscribe({
        next: (link) =>
        {
          this.householdMembersCount = link.length;
          link.forEach(rec =>
          {
            rec.relationship = (rec.relationship !== null ? rec.relationship.displayName : '')
          });
          this.viewHouseholdMembers.setData(link);
          this.viewHouseholdMembers.setSort([{ field: 'date', dir: 'desc' }]);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        },
        complete: () =>
        {
          this.viewHouseholdMembers.stopLoading();
        }
      });
    }
  }

  /**
   * Load Flags for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadFlags(subject: any): void
  {
    this.flagCount = 0;
    this.viewFlags.setData([]);
    if (subject !== null)
    {
      this.viewFlags.startLoading();
      this.flagService.getFlagsForMember(subject.id).subscribe({
        next: (loadedFlags) =>
        {
          this.flagCount = loadedFlags.length;
          loadedFlags.forEach(flag =>
          {
            flag.lastModifiedOn = new Date(flag.lastModifiedOn);
          });
          this.viewFlags.setData(loadedFlags);
          this.viewFlags.setSort([
            { field: 'priority.displayName', dir: 'asc' },
            { field: 'description', dir: 'asc' }
          ]);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        },
        complete: () =>
        {
          this.viewFlags.stopLoading();
        }
      });
    }
  }

  /**
 * Load Goals for the given subject.
 *
 * @param subject Current subject selected.
 */
  private loadGoals(subject: any): void
  {
    if (this.goalsEnabled)
    {
      this.goalCount = 0;
      this.viewGoals.setData([]);
      if (subject !== null)
      {
        this.viewGoals.startLoading();
        this.goalService.getGoalsByMember(subject.id).subscribe({
          next: (loadedGoals) =>
          {
            const goals = [];
            loadedGoals.forEach(d =>
            {
              d.sort = this.goalService.setSort(d);
              goals.push(d);
            });
            loadedGoals = sortBy(goals, ['sort']);
            this.goalCount = loadedGoals.length;
            this.viewGoals.setData(loadedGoals);
          },
          error: (error) =>
          {
            this.messageHandlerService.displayError(error);
          },
          complete: () =>
          {
            this.viewGoals.stopLoading();
          }
        });
      }
    }
  }

  /**
   * Load Extensions for the given subject.
   *
   * @param subject Current subject selected.
  */
  private loadExtensions(subject: any): void
  {
    this.extensionCount = 0;
    this.viewExtensions.setData([]);
    if (subject !== null)
    {
      this.viewExtensions.startLoading();
      this.memberService.getMemberExtensions(subject.id).subscribe({
        next: (loadedExtensions) =>
        {
          const extensions = [];
          loadedExtensions.forEach(d =>
          {
            d.selected = false;
            extensions.push(d);
          });
          loadedExtensions = sortBy(extensions, ['sort']);
          this.extensionCount = loadedExtensions.length;
          this.viewExtensions.setData(loadedExtensions);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        },
        complete: () =>
        {
          this.viewExtensions.stopLoading();
        }
      });
    }
  }

  /**
   * Load Notes for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadNotes(subject: any): void
  {
    this.noteCount = 0;
    this.viewNotes.setData([]);
    if (subject !== null)
    {
      this.viewNotes.startLoading();
      this.noteService.getMemberNotes(subject.id).subscribe({
        next: (loadedNotes) =>
        {
          this.noteCount = loadedNotes.length;
          loadedNotes.forEach(note =>
          {
            note.createdOn = new Date(note.createdOn);
          });
          this.viewNotes.setData(loadedNotes);
          this.viewNotes.setSort([{ field: 'createdOn', dir: 'desc' }]);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        },
        complete: () =>
        {
          this.viewNotes.stopLoading();
        }
      });
    }
  }

  /**
   * Load Cases for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadCases(subject: any): void
  {
    this.caseCount = 0;
    this.viewCases.setData([]);
    if (subject !== null)
    {
      const caseState: State = { skip: 0, take: this.maxLoadedItems, filter: { logic: 'and', filters: [] } };
      caseState.filter.filters.push({ field: 'memberId', operator: 'eq', value: subject.id });
      caseState.filter.filters.push({ field: 'status', operator: 'eq', value: 'Open' });
      this.caseListService.queryByState(caseState, (value: any) =>
      {
        value.data.forEach((d: any) =>
        {
          d.openedOn = d.openedOn != null ? new Date(d.openedOn) : null;
        });
        this.viewCases.setData(value.data);
        this.viewCases.setSort([{ field: 'openedOn', dir: 'desc' }]);
        this.caseCount = value.total;
      });
    }
  }

  /**
   * Load Tasks for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadTasks(subject: any): void
  {
    this.viewTasks.setData([]);
    this.taskCount = 0;
    if (subject !== null)
    {
      const taskState: State = { skip: 0, take: this.maxLoadedItems, filter: { logic: 'and', filters: [] } };
      taskState.filter.filters.push({ field: 'memberId', operator: 'eq', value: subject.id });
      taskState.filter.filters.push({ field: 'closedOn', operator: 'isnull', value: '' });
      this.taskListService.queryByState(taskState, (value: any) =>
      {
        value.data.forEach((d: any) =>
        {
          d.openedOn = d.openedOn != null ? new Date(d.openedOn) : null;
          d.dueDate = d.dueDate != null ? new Date(d.dueDate) : null;
        });
        this.viewTasks.setData(value.data);
        this.viewTasks.setSort([{ field: 'openedOn', dir: 'desc' }]);
        this.taskCount = value.total;
      });
    }
  }

  /**
   * Load Insurances for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadInsurances(subject: any): void
  {
    if (this.insurancesEnabled)
    {
      this.viewInsurances.setData([]);
      this.insuranceCount = 0;
      if (subject !== null)
      {
        const insuranceState: State = { skip: 0, take: this.maxLoadedItems, filter: { logic: 'and', filters: [] }, sort: [] };
        insuranceState.filter.filters.push({ field: 'memberId', operator: 'eq', value: subject.id });
        insuranceState.filter.filters.push({ field: 'deleted', operator: 'eq', value: false });
        insuranceState.sort.push({ field: 'lastModifiedOn', dir: 'desc' });
        this.insuranceListService.queryByState(insuranceState, (value: any) =>
        {
          value.data.forEach((d: any) =>
          {
            d.startDate = d.startDate != null ? new Date(`${d.startDate}T12:00:00.000`) : null;
            d.endDate = d.endDate != null ? new Date(`${d.endDate}T12:00:00.000`) : null;
            d.lastModifiedOn = new Date(d.lastModifiedOn);
          });
          this.viewInsurances.setData(value.data);
          this.viewInsurances.setSort([{ field: 'lastModifiedOn', dir: 'desc' }]);
          this.insuranceCount = value.total;
        });
      }
    }
  }

  /**
   * Load Subject care team for the given subject.
   *
   * @param subject Current subject selected.
   */
  private loadCareTeams(subject: any): void
  {
    this.viewCareTeams.setData([]);
    this.careTeamCount = 0;
    if (subject !== null)
    {
      this.viewCareTeams.startLoading();
      this.teamService.getAllCareTeamForMemberMultipleSources(subject.id).subscribe({
        next: (loadedCareTeams) =>
        {
          const data = [];
          this.careTeamCount = loadedCareTeams[0].length + loadedCareTeams[1].length;

          // Care Team Users
          loadedCareTeams[0].forEach(careTeam =>
          {
            data.push({
              name: careTeam.user.fullName,
              teamRole: careTeam.teamRole.name,
              teamSubRole: careTeam.subRole !== null ? careTeam.subRole.displayName : '',
              organization: careTeam.user.organization !== null ? careTeam.user.organization.name : ''
            });
          });

          // Care Team None Users
          loadedCareTeams[1].forEach(careTeam =>
          {
            data.push({
              name: careTeam.firstName || careTeam.lastName ? `${careTeam.firstName} ${careTeam.lastName}` : `${careTeam.organization}`,
              teamRole: careTeam.roleName,
              teamSubRole: careTeam.subRoleName,
              organization: !careTeam.firstName && !careTeam.lastName ? careTeam.organization : ''
            });
          });
          this.viewCareTeams.setData(data);
          this.viewCareTeams.setSort([{ field: 'name', dir: 'asc' }]);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        },
        complete: () =>
        {
          this.viewCareTeams.stopLoading();
        }
      });
    }
  }
}
